import { useCallback, useRef, useState } from 'react';
import { Button, Input, ModalLayout, TextArea, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { GROUPS_DO_NOT_GRANT_ACCESS_KEY } from '../../constants/copy';
import { useGroupNames } from '../../hooks/useGroupNames';
import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';

/**
 * "New group" modal: name (required, and not one another group already has: said inline while
 * typing, Create stays disabled) + optional description. Failure keeps the modal open with
 * the typed data and raises an error toast; success toasts that the group has no members yet.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(payload: { name: string, description: string }) => Promise<object>} props.onCreate
 *   Rejects with the API message on failure.
 */
function NewGroupModal({ isOpen, onClose, onCreate }) {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const existingNames = useGroupNames(isOpen);
  const trimmed = name.trim();
  const isTaken = trimmed !== '' && existingNames.some((existing) => existing.trim().toLowerCase() === trimmed.toLowerCase());
  const canCreate = trimmed.length > 0 && !isTaken && !submitting;

  const handleClose = useCallback(() => {
    if (submittingRef.current) return;
    setName('');
    setDescription('');
    onClose();
  }, [onClose]);

  const handleCreate = useCallback(async () => {
    if (!canCreate || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const group = await onCreate({ name: name.trim(), description: description.trim() });
      toast.success({
        title: t('groups.newGroup.successTitle'),
        message: t('groups.newGroup.successMessage', { name: group?.name ?? name.trim() }),
        canClose: true,
      });
      setName('');
      setDescription('');
      onClose();
    } catch (err) {
      toast.error({
        title: t('groups.newGroup.errorTitle'),
        message: err?.message ?? t('groups.errors.generic'),
        canClose: true,
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [canCreate, name, description, onCreate, onClose, toast, t]);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      closeDisabled={submitting}
      closeAriaLabel={t('common.close')}
      useBodyPortal
      title={t('groups.newGroup.title')}
      subtitle={t(GROUPS_DO_NOT_GRANT_ACCESS_KEY)}
      panelClassName={NO_HEADER_LINE}
      maxWidthClassName={WIDE_MODAL}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <Input
          label={t('groups.newGroup.nameLabel')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('groups.newGroup.namePlaceholder')}
          error={isTaken ? t('groups.newGroup.nameTaken') : undefined}
          autoFocus
        />
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="new-group-description"
            className="text-sm font-medium text-content-secondary"
          >
            {t('groups.newGroup.descriptionLabel')}
          </label>
          <TextArea
            id="new-group-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t('groups.newGroup.descriptionPlaceholder')}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
        <Button variant="secondary" size="md" onClick={handleClose} disabled={submitting}>
          {t('groups.newGroup.cancel')}
        </Button>
        <Button size="md" disabled={!canCreate} loading={submitting} onClick={handleCreate}>
          {t('groups.newGroup.create')}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default NewGroupModal;
